/** Shared hire thresholds — must match Analytics funnel & backend evaluation. */
export const SCORE_MAX = 10;
export const CUTOFF_PCT = 0.65;
export const CUTOFF_SCORE = SCORE_MAX * CUTOFF_PCT; // 6.5
export const STRONG_HIRE_SCORE = SCORE_MAX * 0.95; // 9.5

/**
 * Session overall is always 0–10. Legacy coding sessions incorrectly stored
 * 0–100 AI/test metrics in overall_score — normalize those for display/hire.
 */
export function normalizeScoreOutOf10(score) {
  const n = Number(score);
  if (!Number.isFinite(n) || n <= 0) return 0;
  if (n > SCORE_MAX) return Math.min(SCORE_MAX, Math.round((n / 10) * 10) / 10);
  return n;
}

export function getSessionScore(session) {
  const n = Number(session?.overall_score ?? session?.score ?? 0);
  return Number.isFinite(n) ? normalizeScoreOutOf10(n) : 0;
}

export function hasScoredSession(session) {
  const raw = session?.overall_score ?? session?.score;
  return raw != null && raw !== "" && Number.isFinite(Number(raw)) && Number(raw) > 0;
}

export function recommendationLabelFromScore(score) {
  const s = normalizeScoreOutOf10(score);
  if (!Number.isFinite(s) || s <= 0) return null;
  if (s < CUTOFF_SCORE) return "No Hire";
  if (s >= STRONG_HIRE_SCORE) return "Strong Hire";
  return "Hire";
}

export function recommendationKeyFromScore(score) {
  const label = recommendationLabelFromScore(score);
  if (label === "Strong Hire") return "strong_yes";
  if (label === "Hire") return "yes";
  if (label === "No Hire") return "no";
  return null;
}

const KEY_TO_LABEL = {
  strong_yes: "Strong Hire",
  yes: "Hire",
  maybe: "Review",
  no: "No Hire",
  strong_no: "Strong No Hire",
};

export function recommendationLabelFromKey(key) {
  if (!key) return "No Hire";
  return KEY_TO_LABEL[key] ?? "No Hire";
}

/** Score-based when a completed score exists; otherwise fall back to stored API value. */
export function getSessionRecommendation(session) {
  if (hasScoredSession(session)) {
    const score = getSessionScore(session);
    return {
      key: recommendationKeyFromScore(score) ?? "no",
      label: recommendationLabelFromScore(score) ?? "No Hire",
    };
  }
  const stored = session?.recommendation ?? session?.hiring_recommendation ?? "no";
  return { key: stored, label: recommendationLabelFromKey(stored) };
}

export const RECOMMENDATION_BADGE = {
  "Strong Hire": { label: "Strong Hire", bg: "#D1FAE5", color: "#065F46" },
  Hire: { label: "Hire", bg: "#FEF3C7", color: "#92400E" },
  "No Hire": { label: "No Hire", bg: "#FEE2E2", color: "#991B1B" },
  Review: { label: "Review", bg: "#FEF3C7", color: "#92400E" },
  "Strong No Hire": { label: "Strong No Hire", bg: "#FEE2E2", color: "#7F1D1D" },
};

export function getRecommendationBadge(session) {
  const { label } = getSessionRecommendation(session);
  return RECOMMENDATION_BADGE[label] ?? RECOMMENDATION_BADGE["No Hire"];
}

export function recCriteriaText() {
  return `No Hire < ${CUTOFF_SCORE} · Hire ≥ ${CUTOFF_SCORE} · Strong Hire ≥ ${STRONG_HIRE_SCORE}`;
}
